import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Avatar, Box, Button, Dialog, IconButton, TextField, Typography } from "@mui/material";

import { UserInfo } from "../../../../types";
import { ClosePopupIcon } from "../../../../assets/icons/MUI/EntitiesIcons";
import { colors } from "../../../../theme/palette";
import { useProfileData } from "../../../../hooks/useProfileData";
import { getChatThreadRoute } from "../../../../routers/routeUtils";
import { createChat } from "../../../../pages/Chat/firestore";
import { useDataFormatter } from "../../../../hooks/useDataFormatter";
import AutoCompleteSearch from "../../../../shared/NewChatPopUp/AutoCompleteSearch";

const CreateNewChatPopUp = ({
  open,
  handleClose,
  receiverInfo,
}: {
  open: boolean;
  handleClose: () => void;
  receiverInfo?: UserInfo;
}) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState<boolean>(false);
  const [searchedReceiverName, setSearchedReceiverName] = useState<string | null>(null);
  const [searchedReceiverId, setSearchedReceiverId] = useState<number | null>(null);
  const [messageError, setMessageError] = useState<boolean>(false);
  const navigate = useNavigate();
  const { data } = useProfileData();
  const { fullName: senderName, id: senderId } = data;
  const {
    fullName: receiverFullName,
    firstTwoChr: receiverFirstTwoChr,
    media: receiverMedia,
    role: receiverRole,
    id: receiverId,
  } = useDataFormatter(receiverInfo);

  const handleSendMessage = async () => {
    setIsSending(true);
    const selectedReceiverId = receiverId || searchedReceiverId;
    const selectedReceiverName = receiverFullName || searchedReceiverName;

    if (!message || message.length === 0) {
      setMessageError(true);
      return;
    }
    setMessageError(false);
    if (!senderId || !selectedReceiverId || !senderName || !selectedReceiverName) return;
    const threadId = await createChat(message, senderId, selectedReceiverId, senderName, selectedReceiverName);
    navigate(getChatThreadRoute(threadId, selectedReceiverId));
    setIsSending(false);
    handleClose();
  };

  const isSendButtonDisabled = isSending || message.length === 0 || !searchedReceiverId || !searchedReceiverName;
  return (
    <Dialog onClose={handleClose} open={open}>
      <Box sx={styles.container}>
        <IconButton sx={styles.closeButton} onClick={handleClose}>
          <ClosePopupIcon />
        </IconButton>
        <Typography sx={styles.headerTitle} component="h4">
          {t("components.newChatPopUp.newMessage")}
        </Typography>
        {receiverInfo && (
          <Box sx={styles.receiverInfo}>
            <Avatar style={{ width: "48px", height: "48px" }} src={receiverMedia}>
              {receiverFirstTwoChr}
            </Avatar>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={styles.receiverName}>{receiverFullName}</Typography>
              <Typography sx={styles.receiverRole}>{receiverRole}</Typography>
            </Box>
          </Box>
        )}
        {!receiverInfo && (
          <AutoCompleteSearch
            setSearchedReceiverName={setSearchedReceiverName}
            setSearchedReceiverId={setSearchedReceiverId}
          />
        )}
        <Box sx={styles.messageContainer}>
          <TextField
            fullWidth
            sx={styles.messageInput}
            value={message}
            multiline
            rows={4}
            placeholder={t("components.newChatPopUp.writeAMessage")}
            error={messageError}
            helperText={messageError ? t("components.newChatPopUp.writeYourMessage") : ""}
            onChange={(event) => {
              setMessage(event.target.value);
              if (event.target.value.length === 0) {
                setMessageError(true);
              } else {
                setMessageError(false);
              }
            }}
          />
          <Button
            variant="primary-sm"
            sx={styles.sendButton}
            onClick={handleSendMessage}
            disabled={isSendButtonDisabled}
          >
            {t("components.newChatPopUp.send")}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CreateNewChatPopUp;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    width: "435px",
    borderRadius: "12px",
    background: "#FFF",
  },
  closeButton: {
    position: "absolute",
    top: "16px",
    right: "16px",
  },
  headerTitle: {
    display: "block",
    borderBottom: "1px solid #EAECF0",
    padding: "21px 0 18px",
    marginBottom: "16px",
    textAlign: "center",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: "normal",
  },
  receiverInfo: {
    display: "flex",
    columnGap: "12px",
    alignItems: "flex-start",
    margin: "0 28px 32px",
  },
  receiverName: {
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "24px",
  },
  receiverRole: {
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "normal",
    color: "#707070",
  },
  messageContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    margin: "0 28px 16px",
    alignItems: "flex-end",
  },
  messageInput: {
    "& .MuiInputBase-root": {
      borderRadius: "8px !important",
      paddingInlineEnd: "0 !important",
    },
  },
  sendButton: {
    width: "fit-content",
  },
};
